/**
 * MobileDrawer — drawer laterale sinistro per la navigazione PWA mobile.
 *
 * Andrea 2026-05-23: la TabBar in basso ha solo 5 tab principali
 * (Home/Rose/Calendario/Classifica/Squadra). Tutte le altre pagine
 * (admin, tools squadra avanzate, consultazione) stanno qui dentro,
 * raggruppate per categoria.
 *
 * Aperto via hamburger button nell'AppHeader. Chiude su:
 *   - tap su backdrop
 *   - tap su un link
 *   - swipe sinistra (futuro)
 */

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { etichetta } from "../common/etichette";
import { LegaMenu } from "./LegaMenu";
import { adminAppVisibili, adminLegaVisibili } from "./adminLinks";
import { AvatarUtente } from "../profilo/AvatarUtente";
import { SelettoreTema } from "../common/SelettoreTema";
import { tr } from "../../i18n";

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface DrawerLink {
  to: string;
  label: string;
  icon: string;
  /** Se valorizzato, mostra il link SOLO se l'utente ha uno di questi
   * ruoli (lega o aggiuntivi). undefined = visibile a tutti. */
  roles?: string[];
}

interface DrawerSection {
  title: string;
  links: DrawerLink[];
}

// "La tua squadra" — strumenti che il presidente/secondo allenatore usa
const SQUADRA_LINKS: DrawerLink[] = [
  { to: "/formazione", label: "Schiera formazione", icon: "🎯" },
  { to: "/budget", label: "Budget", icon: "💰" },
  { to: "/mercato", label: "Mercato", icon: "⇄" },
  { to: "/rinnovi", label: "Rinnovi", icon: "🔄" },
  { to: "/prestiti", label: "Prestiti", icon: "🤝" },
  { to: "/storico", label: "Storico", icon: "📜" },
];

// Consultazione lega
const CONSULTAZIONE_LINKS: DrawerLink[] = [
  { to: "/scommesse", label: "Squad Bet", icon: "🎰" },
  { to: "/gazzetta", label: "Squad Magazine", icon: "📰" },
  { to: "/giocatori", label: "Catalogo giocatori", icon: "📋" },
  { to: "/regole", label: "Regole", icon: "📖" },
  { to: "/notifiche", label: "Notifiche", icon: "🔔" },
];

// Altro
const ALTRO_LINKS: DrawerLink[] = [
  { to: "/leghe", label: "Le mie leghe", icon: "🏟️" },
  { to: "/squadcup", label: "SquadCup", icon: "🦅" },
  { to: "/privacy", label: "Privacy", icon: "🔒" },
];

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  /** Andrea 2026-05-23: dopo navigazione dal drawer, back torna a "/". */
  function navigateAppLike(to: string) {
    if (to === "/") {
      navigate("/", { replace: true });
      return;
    }
    navigate("/", { replace: true });
    navigate(to);
  }

  const sections: DrawerSection[] = [
    { title: tr("La tua squadra"), links: SQUADRA_LINKS },
    { title: tr("Consultazione"), links: CONSULTAZIONE_LINKS },
    { title: tr("Admin · Lega"), links: adminLegaVisibili(user) },
    { title: tr("Admin · App"), links: adminAppVisibili(user) },
    { title: tr("Altro"), links: ALTRO_LINKS },
  ].filter((s) => s.links.length > 0);

  // Andrea 2026-06-13: sezioni a fisarmonica (espandi/riduci).
  // Andrea 2026-09-24: all'apertura sono aperte "La tua squadra" e
  // "Consultazione"; le due sezioni admin e "Altro" partono chiuse, e
  // quelle admin restano chiuse anche quando si e' su una loro pagina
  // (si aprono solo a mano).
  const [openSections, setOpenSections] = useState<Set<string>>(
    () => new Set([tr("La tua squadra"), tr("Consultazione")]),
  );

  useEffect(() => {
    const all: DrawerSection[] = [
      { title: tr("La tua squadra"), links: SQUADRA_LINKS },
      { title: tr("Consultazione"), links: CONSULTAZIONE_LINKS },
      { title: tr("Altro"), links: ALTRO_LINKS },
    ];
    const active = all.find((s) =>
      s.links.some((l) => pathname === l.to || pathname.startsWith(l.to + "/")),
    );
    if (active) {
      setOpenSections((prev) => (prev.has(active.title) ? prev : new Set(prev).add(active.title)));
    }
  }, [pathname]);

  function toggleSection(title: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-50 bg-black/60 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] bg-bg-card border-r-2 border-accent/40 shadow-2xl transform transition-transform overflow-y-auto flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
      >
        {/* Header drawer */}
        <div className="bg-primary-deep px-4 py-3 border-b border-accent/30 flex items-center justify-between sticky top-0 z-10">
          <div className="font-display text-accent text-xl tracking-wide">FantaSquad</div>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-fg text-2xl leading-none"
            aria-label={tr("Chiudi menu")}
          >
            ×
          </button>
        </div>

        {/* User info */}
        {user && (
          <div className="px-4 py-3 border-b border-primary-soft/30">
            <div className="text-xs text-fg-muted uppercase tracking-wider">
              {tr("Connesso come")}
            </div>
            {/* Andrea 2026-09-29: tocca nome/foto per aprire il Profilo */}
            <Link
              to="/profilo"
              onClick={onClose}
              className="flex items-center gap-2 mt-1 group"
              aria-label={tr("Apri il profilo")}
            >
              <AvatarUtente user={user} size={32} />
              <span className="font-bold text-fg truncate group-hover:text-accent">
                {user.nome_completo}
              </span>
              <span className="ml-auto text-[10px] text-accent/80 shrink-0">{tr("Profilo ›")}</span>
            </Link>
            <div className="text-[10px] text-fg-muted font-mono mt-0.5">
              {etichetta(user.ruolo_lega)}
              {user.ruoli_aggiuntivi && user.ruoli_aggiuntivi.length > 0 && (
                <> · +{user.ruoli_aggiuntivi.join(", ")}</>
              )}
            </div>
            {/* Andrea 2026-09-14: lega corrente, si apre per cambiarla o crearne una */}
            <LegaMenu variante="mobile" onScelta={onClose} />
            {/* Andrea 2026-10-08: tema chiaro/scuro */}
            <div className="mt-2">
              <SelettoreTema compatto />
            </div>
          </div>
        )}

        {/* Sezioni link — a fisarmonica (Andrea 2026-06-13) */}
        <nav className="flex-1 py-2">
          {sections.map((sec) => {
            const isOpen = openSections.has(sec.title);
            return (
              <div key={sec.title} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleSection(sec.title)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-accent hover:bg-primary-soft/15 transition"
                >
                  <span>{tr(sec.title)}</span>
                  <span
                    className={`text-[11px] leading-none transition-transform duration-200 ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  >
                    ▶
                  </span>
                </button>
                {isOpen && (
                  <ul>
                    {sec.links.map((l) => {
                      const active = pathname === l.to || pathname.startsWith(l.to + "/");
                      return (
                        <li key={l.to}>
                          <Link
                            to={l.to}
                            onClick={(e) => {
                              e.preventDefault();
                              onClose();
                              if (!active) navigateAppLike(l.to);
                            }}
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm transition ${
                              active
                                ? "bg-accent/10 text-accent font-bold border-r-2 border-accent"
                                : "text-fg hover:bg-primary-soft/20"
                            }`}
                          >
                            <span className="text-lg leading-none w-6 text-center">{l.icon}</span>
                            <span>{tr(l.label)}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout in fondo */}
        <div className="border-t border-primary-soft/30 px-4 py-3 sticky bottom-0 bg-bg-card">
          <button
            type="button"
            onClick={() => {
              onClose();
              logout();
            }}
            className="w-full flex items-center gap-3 px-2 py-2 text-sm text-danger hover:bg-danger/10 rounded transition"
          >
            <span className="text-lg leading-none">⎋</span>
            <span className="font-bold">{tr("Esci")}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
